import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, IconButton } from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import TableHeaderTextField from "../../../../components/table-header-text-filed/table-header-text-filed";
import TableHeaderDatePicker from "../../../../components/table-header-datepicker/table-header-datepicker";
import TableHeaderSelectField from "../../../../components/table-header-select-field/table-header-select-field";
import Table from "../../../../components/table/table";
import { getOrderIssuesRequest, getOrderIssuesSuccess } from "../../../../redux/orders/slice";
import IssueDetails from "../../issues/issue-details/issue-details";
import AcceptanceIssueDetails from "../../issues/acceptance-issue-details/acceptance-issue-details";
import { userType } from "../../../../utils/Constant";

export default function OrderIssues({ orderId }: any) {
  const dispatch = useDispatch();
  const [rowData, setRowData] = useState<any>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [filterParams, setFilterParams] = useState({});
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [showIssueDetails, setShowIssueDetails] = useState(false);

  const { loading, orderIssues } = useSelector((state: any) => state.orders);
  const { currentUser } = useSelector((state: any) => state.user.user);

  const rowCountRef = useRef(orderIssues?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (orderIssues?.totalCount !== undefined) {
      rowCountRef.current = orderIssues?.totalCount;
    }
    return rowCountRef.current;
  }, [orderIssues?.totalCount]);

  const column: GridColDef[] = [
    {
      field: "createdOrUpdatedAt",
      headerName: "Last Updated",
      flex: 1,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY, h:mma") : " ",
      renderHeader: (params) => (
        <TableHeaderDatePicker
          field="createdOrUpdatedAt"
          sortField="last_updated"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "type",
      headerName: "Issue Type",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="type"
          sortField="type"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={["Tenant", "Client", "Inspector"]}
        />
      ),
    },
    {
      field: "description",
      headerName: "Issue",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="description"
          sortField="description"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "isResolved",
      headerName: "Status",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Resolved" : "Unresolved"}
          color={params.value ? "success" : "error"}
        />
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="isResolved"
          sortField="is_resolved"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={["Resolved", "Unresolved"]}
        />
      ),
    },
  ];

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    onFilterUsers({ sortColumn: "is_resolved", sortOrder: "asc" });
    dispatch(getOrderIssuesSuccess({ response: null }));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (orderIssues) {
      setRowData(orderIssues?.items);
    }
  }, [orderIssues]);

  useEffect(() => {
    if (paginationModel) {
      const pagination = { ...paginationModel };
      pagination.page = paginationModel?.page + 1;
      setFilterParams({ ...filterParams, ...pagination });
    }
  }, [paginationModel]); // eslint-disable-line

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      dispatch(getOrderIssuesRequest({ orderId, ...filters }));
    }
  }, [filterParams]); // eslint-disable-line

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterUsers = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          //if isActive status is false and if userType is 0 then condition also should pass
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  return (
    <Box sx={{ mx: 2, my: 3, border: "1px solid #C4C4C4", borderRadius: 1, pb: 2 }}>
      <Box
        className="clients-header"
        sx={{
          mt: 2,
          mb: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display={"flex"} alignItems={"center"}>
          <Button
            sx={{ fontWeight: "550" }}
            variant="text"
            disabled={rowSelectionModel.length !== 1}
            onClick={() => setShowIssueDetails(true)}
          >
            View/Update Issue
          </Button>
        </Box>
        <Box>
          <IconButton
            aria-label="filter"
            onClick={() => onFilterClick(!showTableFilter)}
          >
            {showTableFilter ? (
              <FilterAltOffOutlinedIcon color="primary" />
            ) : (
              <FilterAltOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          rowData={rowData}
          rowCount={rowCount}
          loading={loading}
          columnDef={column}
          headerHeight={headerHeight}
          disableColumnMenu={true}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          checkboxSelection={true}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
        />
      </Box>

      {currentUser?.userType === userType.tenant && showIssueDetails && (
        <IssueDetails
          issueId={
            rowSelectionModel &&
            rowSelectionModel.length > 0 &&
            rowSelectionModel[0]
          }
          orderId={orderId}
          showDialog={showIssueDetails}
          closeDialog={() => setShowIssueDetails(false)}
        />
      )}

      {currentUser?.userType === userType.inspector && showIssueDetails && (
        <AcceptanceIssueDetails
          issueId={
            rowSelectionModel &&
            rowSelectionModel.length > 0 &&
            rowSelectionModel[0]
          }
          orderId={orderId}
          showDialog={showIssueDetails}
          closeDialog={() => setShowIssueDetails(false)}
        />
      )}
    </Box>
  );
}
